import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import defaultTimelineFilters from '@/constants/defaultTimelineFilters';
import { STICKY_CHROME_CLASS } from '@/constants/layout';
import {
  ACHIEVEMENT_ACTION_FILTERS,
  APP_OPEN_ACTION_FILTERS,
  APP_UNINSTALL_ACTION_FILTERS,
  APPOINTMENT_ACTION_FILTERS,
  CHALLENGE_ACTION_FILTERS,
  DRUG_SCREEN_ACTION_FILTERS,
  ENGAGEMENT_ACTIVITY_ACTION_FILTERS,
  EPISODE_OF_CARE_ACTION_FILTERS,
  LOG_IN_ACTION_FILTERS,
  LOG_OUT_ACTION_FILTERS,
  MESSAGE_ACTION_FILTERS,
  MISSION_ACTION_FILTERS,
  NOTE_ACTION_FILTERS,
  PERSON_MERGE_ACTION_FILTERS,
  PHASE_ACTION_FILTERS,
  PROGRAM_QUALIFICATION_ACTION_FILTERS,
  REFERRAL_ACTION_FILTERS,
  SIGN_UP_ACTION_FILTERS,
  SURVEY_ACTION_FILTERS,
  TASK_ACTION_FILTERS
} from '@/constants/timelineFilters';
import TimelineFilterForm from '@/forms/TimelineFilterForm';
import { ACTION_FILTERS, type TimelineFilters } from '@/forms/TimelineFilterForm/types';
import useAPI from '@/hooks/useAPI';
import usePagination from '@/hooks/usePagination';
import useQueryString from '@/hooks/useQueryString';
import useUserSetting from '@/hooks/useUserSetting';
import PersonContext from '@/pages/Person/PersonContext';
import TimelineItem from '@/pages/Timeline/TimelineItem';
import ActivityLogsRepository from '@/repositories/ActivityLogsRepository';
import type { ActivityLog } from '@/types/activityLog';
import Container from '@/ui/components/Container';
import Heading from '@/ui/components/Heading/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import getInitialFilters from '@/utils/getInitialFilters';

const Timeline = () => {
  const {
    params: queryParams,
    setParams,
    hasQueryParams
  } = useQueryString<Partial<TimelineFilters>>();
  const [savedFilters, setSavedFilters] = useUserSetting('timelineFilters', {});
  const [filters, setFilters] = useState<Partial<TimelineFilters>>(() => {
    // Precedence is unchanged: query params, then the saved filters, then
    // defaults. A link someone was sent still wins over what they last used.
    if (hasQueryParams) {
      return queryParams;
    }
    return getInitialFilters<TimelineFilters>({
      storedFilters: savedFilters as Partial<TimelineFilters>,
      defaultFilters: defaultTimelineFilters
    });
  });
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const { currentPage, goToPage, nextPage, prevPage } = usePagination(page, totalPages);
  const { person } = useContext(PersonContext);
  const activityLogsRepository = ActivityLogsRepository();

  // Filters seeded from the store (or from the defaults) are not a user edit.
  // Saving them would queue a pending change that outranks the server copy
  // still in flight, so on a machine with a cold cache the defaults would be
  // pushed back over the user's saved filters. Held by identity rather than as
  // a "first run" flag because this effect also reruns when `setParams` changes.
  // Filters that came from a link are a real choice, so those are saved as usual.
  const seededFilters = useRef(hasQueryParams ? null : filters);

  useEffect(() => {
    setPage(1); // Prevent fetching invalid page
    setParams(filters);

    if (filters !== seededFilters.current) setSavedFilters(filters);
    // setSavedFilters is stable per key; including it would loop on every
    // store notification.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, setParams]);

  const actionFilters = useMemo(() => {
    const action: string[] = [];

    if (filters.action?.includes(ACTION_FILTERS.ACHIEVEMENT)) {
      action.push(...ACHIEVEMENT_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.APPOINTMENT)) {
      action.push(...APPOINTMENT_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.APP_OPEN)) {
      action.push(...APP_OPEN_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.APP_UNINSTALL)) {
      action.push(...APP_UNINSTALL_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.CHALLENGE)) {
      action.push(...CHALLENGE_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.DRUG_SCREEN)) {
      action.push(...DRUG_SCREEN_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.ENGAGEMENT_ACTIVITY)) {
      action.push(...ENGAGEMENT_ACTIVITY_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.EPISODE_OF_CARE)) {
      action.push(...EPISODE_OF_CARE_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.LOG_IN)) {
      action.push(...LOG_IN_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.LOG_OUT)) {
      action.push(...LOG_OUT_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.MESSAGE)) {
      action.push(...MESSAGE_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.MISSION)) {
      action.push(...MISSION_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.NOTE)) {
      action.push(...NOTE_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.PERSON_MERGE)) {
      action.push(...PERSON_MERGE_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.PHASE)) {
      action.push(...PHASE_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.PROGRAM_QUALIFICATION)) {
      action.push(...PROGRAM_QUALIFICATION_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.REFERRAL)) {
      action.push(...REFERRAL_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.SIGN_UP)) {
      action.push(...SIGN_UP_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.SURVEY)) {
      action.push(...SURVEY_ACTION_FILTERS);
    }

    if (filters.action?.includes(ACTION_FILTERS.TASK)) {
      action.push(...TASK_ACTION_FILTERS);
    }

    if (Array.isArray(filters.action)) {
      filters.action.forEach((filter) => {
        if (filter?.includes('note_created')) {
          action.push(filter);
        }
      });
    }

    return action;
  }, [filters.action]);

  const { data, isPending, isSuccess } = useAPI({
    queryKey: ['getTimelineData', person.id, filters, page],
    queryFn: async () => {
      return await activityLogsRepository.getActivityLogsForPerson(person.id, {
        ...filters,
        action: actionFilters,
        page: page.toString()
      });
    }
  });

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (isSuccess) {
      const { page, total_pages } = data;

      setPage(page);
      setTotalPages(total_pages);
    }
  }, [isSuccess, data]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString([], { dateStyle: 'medium', timeStyle: 'medium' });
  };

  return (
    <Container type="page">
      <div className={`mb-4 pt-1 z-19 bg-gray-100 ${STICKY_CHROME_CLASS.pageSection}`}>
        <PageHeader headingText="Timeline" className="sr-only" />
        <TimelineFilterForm activeFilters={filters} setFilters={setFilters} />
      </div>
      <Pagination
        currentPage={page}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}>
        {isPending ? (
          <LoadingSkeleton spinnerProps={{ label: 'Loading timeline...' }} />
        ) : (
          <>
            {data?.results?.length ? (
              <div className="space-y-4 my-2" data-testid="timeline-results">
                {data?.results?.map((activityLog: ActivityLog) => {
                  return <TimelineItem key={activityLog.id} activityLog={activityLog} />;
                })}
              </div>
            ) : (
              <div className="text-center py-4">
                <Heading text="No Timeline Data" value={2} />
                {filters.created_at__gte && <>from {formatDate(filters.created_at__gte)}</>}
                {filters.created_at__lte && (
                  <>
                    {filters.created_at__gte && <br />}
                    to {formatDate(filters.created_at__lte)}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </Pagination>
    </Container>
  );
};

export default Timeline;
